import { useEffect, useMemo, useRef } from "react";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";

import { CustomerSettings } from "../components/customer-settings/index.tsx";
import { AutoDispatchGrid } from "../components/grid/index.tsx";
import { Header } from "../components/header/index.tsx";
import { useAutoDispatchNavigation } from "../hooks/use-auto-dispatch-navigation.ts";
import { useGetColumns } from "../hooks/use-get-columns.tsx";
import { useGetCustomerCarriers } from "../hooks/use-get-customer-carriers.ts";
import { useGetCustomerTenderSettings } from "../hooks/use-get-customer-tender-settings.ts";
import { useUpdateCustomerCarrier } from "../hooks/use-update-customer-carrier.ts";
import type { CustomerTenderSettingChange } from "../hooks/use-update-customer-tender-settings.ts";
import { useUpdateCustomerTenderSetting } from "../hooks/use-update-customer-tender-settings.ts";
import { autoDispatchGridSection } from "./index.module.css";

export const CustomerLayout = () => {
	const { selectedCustomer, handleSelectCustomer, isCustomerLoading } =
		useAutoDispatchNavigation();
	const { showError } = useNotificationActions();
	const hasShownTenderSettingsErrorRef = useRef(false);

	const {
		enabledCarriers,
		enabledCarriersIsLoading,
		enabledCarriersIsFetching,
		refetchEnabledCarriers,
	} = useGetCustomerCarriers(selectedCustomer?.id ?? 0);

	const {
		customerTenderSettings,
		customerTenderSettingsError,
		customerTenderSettingsIsLoading,
		customerTenderSettingsIsFetching,
		refetchCustomerTenderSettings,
	} = useGetCustomerTenderSettings(selectedCustomer?.id ?? 0);

	useEffect(() => {
		if (customerTenderSettingsError) {
			if (!hasShownTenderSettingsErrorRef.current) {
				showError(
					"Failed to load customer tender settings. Refresh to try again.",
				);
				hasShownTenderSettingsErrorRef.current = true;
			}
		} else {
			hasShownTenderSettingsErrorRef.current = false;
		}
	}, [customerTenderSettingsError, showError]);

	const areTenderSettingsDisabled =
		!selectedCustomer ||
		customerTenderSettingsIsLoading ||
		customerTenderSettingsError != null ||
		customerTenderSettings == null;

	const updateCarrier = useUpdateCustomerCarrier(selectedCustomer?.id ?? 0);
	const updateCustomerSettings = useUpdateCustomerTenderSetting(
		selectedCustomer?.id ?? 0,
	);

	const columns = useGetColumns({
		isLoading: customerTenderSettingsIsLoading || enabledCarriersIsLoading,
		updateCarrier: () => {},
		updateCustomerCarrier: updateCarrier,
		isCustomerGrid: true,
	});

	const memoizedColumns = useMemo(
		() => columns.filter((col) => col.group.includes("customer")),
		[columns],
	);

	// Default the grid to alphabetical order by carrier name so users always
	// land on a predictable view. Users can still re-sort via column headers.
	const sortedCarriers = useMemo(
		() =>
			[...(enabledCarriers ?? [])].sort((a, b) =>
				(a.carrierName ?? "").localeCompare(b.carrierName ?? ""),
			),
		[enabledCarriers],
	);

	const handleSettingChange = (change: CustomerTenderSettingChange) => {
		return updateCustomerSettings(change);
	};

	const handleRefresh = () => {
		refetchEnabledCarriers();
		refetchCustomerTenderSettings();
	};

	return (
		<TraxPageLayout>
			{" "}
			<TraxPageHeader title={"Auto Dispatch"} />
			<div>
				<Header
					onCustomerChange={(customer) => {
						handleSelectCustomer(customer ?? undefined);
					}}
					onHandleRefresh={handleRefresh}
					selectedCustomer={selectedCustomer}
				/>
			</div>
			{isCustomerLoading ? (
				<LoadingSpinner />
			) : (
				<div className={autoDispatchGridSection}>
					<CustomerSettings
						customerTenderSettings={customerTenderSettings ?? undefined}
						isDisabled={areTenderSettingsDisabled}
						onHandleChange={handleSettingChange}
					/>
					<hr />
					<AutoDispatchGrid
						columns={memoizedColumns}
						data={sortedCarriers}
						isLoading={
							isCustomerLoading ||
							enabledCarriersIsLoading ||
							enabledCarriersIsFetching ||
							customerTenderSettingsIsFetching
						}
					/>
				</div>
			)}
		</TraxPageLayout>
	);
};
